import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { STUDIO_THEMES, type StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { TypedMemo } from '../../../common/types';
import { useGetReleaseKitQuery } from '../../../../redux/api/fymAPI';
import { CatalogItem, SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import { useSMLRouter } from '../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../routing/sml/SMLRoutes';
import { useQueryParam } from '../../../../utils/hooks/useQueryParam';
import { Panel } from '../../components/primitives/Panel';
import { CatalogSongField } from '../song/CatalogSongField';
import { useOpenSongPage } from '../../../smartLinks/catalog/song/page/useOpenSongPage';
import type { SongLyricsView } from '../../../smartLinks/catalog/song/page/catalogSongPath';
import { ReelDraft } from './aiReel/useReelDraft';
import { useReelActions } from './aiReel/useReelActions';
import { StudioSongFacts } from './details/props';
import { AiReelDetails } from './details/AiReelDetails';
import { DetailsStep } from './details/DetailsStep';
import { ImageDetailsStep } from './details/ImageDetailsStep';
import { SpotifyHeaderDetailsStep } from './spotifyHeader/SpotifyHeaderDetailsStep';
import { StudioDraft } from './useStudioDraft';
import { STUDIO_SONG_FIELD_ID, useStudioMake } from './useStudioMake';
import { useStudioItems } from './useStudioItems';
import { useStudioPlan } from './useStudioPlan';
import { useFrameLyric } from './frames/useFrameLyric';
import { StudioSongRow } from './StudioSongRow';
import { StudioSteps } from './StudioSteps';
import { AssetTypeScreen } from './AssetTypeScreen';
import { StudioMade, StudioMadeBanner } from './StudioMadeBanner';
import { studioThemeMessages } from './studioThemeMessages';
import { useIsKitLookSaving } from './useIsKitLookSaving';
import { useSongTheme } from './useSongTheme';
import { StudioThemeChip } from './StudioThemeChip';
import { useThemeStillSong } from '../releases/theme/useThemeStillSong';
import { useStudioWorkActions } from '../work/useStudioWorkActions';
import { workFilesOf, workProgressParams } from '../work/workProgressLink';
import { useStudioCardWork } from './useStudioCardWork';
import { StudioCardWork } from './studioCardWork';
import { useStudioActionFeedback } from './useStudioActionFeedback';
import { releaseAssetParams } from '../releases/releasePageLink';
import { STUDIO_PUBLISH_FIELD_ID, useStudioPublish } from '../publish/useStudioPublish';
import { StudioPublishSwitch } from '../publish/StudioPublishSwitch';
import { useStudioMusicVideoEntry } from './useStudioMusicVideoEntry';
import { DirectedVideoStudioCard } from './DirectedVideoStudioCard';

interface StudioSongViewProps {
  accountId: number;
  item: CatalogItem;
  song: StudioSongFacts;
  draft: StudioDraft;
  reelDraft: ReelDraft;
  reelActions: ReturnType<typeof useReelActions>;
  /** Reads the catalog again after a 400 `M24_NO_MASTER`. */
  onNoMaster: () => void;
}

/**
 * The Studio for one song (designs l1–l4, n1–n6, q3a, release-kit t2): the song row with the
 * theme chip of the song, the steps, the song facts with a link to Catalog when the master is
 * missing (the master is added only on the song page in Catalog), then
 * the step on screen: every asset type on one screen (t2), or the details of one. "Make it" goes
 * back to the asset types and shows the "Making it" banner with a link to the Work page (t3); the
 * Studio keeps no strip of the work. A made asset opens on the Assets tab of its release. Step 3
 * sends the style that it shows.
 */
const StudioSongViewComponent: React.FC<StudioSongViewProps> = (props) => {
  const { accountId, item, song, draft, reelDraft, reelActions, onNoMaster } = props;
  const { step, format, themeId, details } = draft;
  // One-off publish (owner decision 2026-10-09): the "Publish to YouTube when ready" switch of a
  // full-song video, and the watch for the "Publish to YouTube?" toast.
  // The assets of the last "Make it": the banner marks the row of one asset on the Work page.
  const madeUuids = useRef<string[]>([]);
  const publish = useStudioPublish({ accountId, catalogItemUuid: item.uuid, releaseDate: item.releaseDate, themeId });
  const make = useStudioMake({
    accountId,
    catalogItemUuid: item.uuid,
    onNoMaster,
    onCreated: (created) => {
      madeUuids.current = created.map((asset) => asset.uuid);
      publish.afterMade(created);
    },
  });
  const { reset, clearFeedback } = make;
  const isLookSaving = useIsKitLookSaving();
  const songTheme = useSongTheme(accountId, item.uuid);
  const stillSong = useThemeStillSong(item);
  const frameLyric = useFrameLyric({ accountId, itemUuid: item.uuid, song: { ...song, title: item.title } });
  const previewTheme = useMemo(
    () => ({ look: songTheme.look, palette: songTheme.palette, song: stillSong, lyric: frameLyric }),
    [frameLyric, songTheme.look, songTheme.palette, stillSong]
  );
  // The plan (Free has no full videos, design s7c).
  const plan = useStudioPlan(accountId);
  const songContext = useMemo(
    () => ({ ...song, canRenderFullSongVideos: plan.canRenderFullSongVideos }),
    [plan.canRenderFullSongVideos, song]
  );
  // The music video card opens the editor (spec 2026-10-09 D11): no step 3.
  const pickTheme = useStudioMusicVideoEntry({
    accountId,
    catalogItemUuid: item.uuid,
    themeId,
    onPick: draft.pickTheme,
  });
  // "Add the lyrics", "Check the timing" and "Fix the times" open the Lyrics section of the song
  // page in Catalog. The master, the lyrics and the times change only there (owner rule 2026-10-06).
  const openSongPage = useOpenSongPage();
  const openLyrics = useCallback(
    (view?: SongLyricsView) => openSongPage(item.uuid, 'lyrics', view),
    [item.uuid, openSongPage]
  );
  useEffect(() => reset(), [reset, item.uuid, themeId]);
  // A failed "Make it" or Generate stays under the button until the user changes a field or tries
  // again. The keys compare the values, so a new object with the same choices keeps the message.
  const detailsKey = useMemo(() => JSON.stringify(details), [details]);
  useEffect(() => clearFeedback(), [clearFeedback, detailsKey]);
  const reelOptionsKey = useMemo(() => JSON.stringify(reelDraft.createOptions ?? null), [reelDraft.createOptions]);
  const clearCreateError = useRef(reelActions.clearCreateError);
  clearCreateError.current = reelActions.clearCreateError;
  useEffect(() => clearCreateError.current(), [reelOptionsKey]);
  // A failed Generate shows under the button. A refusal (the plan, the song) has its own notice.
  const reelError =
    reelActions.refusal === null && reelActions.songRefusal === null ? reelActions.createError : undefined;
  const reelFeedback = useStudioActionFeedback('generate the AI reel', reelError);

  // A made asset opens in the viewer on the Assets tab of its release; the Studio has no result
  // page. An old `?result=<assetUuid>` link (Work page, before 2026-10-07) goes there too.
  const intl = useIntl();
  const router = useSMLRouter();
  const resultUuid = useQueryParam('result', undefined, '');
  const [made, setMade] = useState<StudioMade | null>(null);
  useEffect(() => {
    setMade(null);
  }, [item.uuid]);
  const openAsset = useCallback(
    (asset: SerializedKitAsset) =>
      router.push(SMLRoutes.promoteRelease, releaseAssetParams(item.uuid, asset.uuid, undefined, asset.themeId)),
    [item.uuid, router]
  );
  useEffect(() => {
    if (resultUuid !== '') {
      // replace, not push: Back must not return to the old link and redirect again.
      router.replace(SMLRoutes.promoteRelease, releaseAssetParams(item.uuid, resultUuid));
    }
  }, [item.uuid, resultUuid, router]);
  const workActions = useStudioWorkActions({
    accountId,
    item,
    onOpenAsset: openAsset,
    onRetryReel: reelActions.tryAgain,
    onChangeReelSong: reelActions.changeSong,
  });
  // The live state of each asset type on its step-2 card (owner request 2026-10-06).
  const cardWork = useStudioCardWork(accountId, item.uuid);
  // "See the work" of a card opens the Work page with the work of the song (owner, 2026-10-11;
  // design assets-rethink f4). One file of the card: its row has the mark.
  const kit = useGetReleaseKitQuery({ accountId, catalogItemUuid: item.uuid });
  const kitAssets = kit.data?.assets;
  const kitReels = kit.data?.reels;
  const openCardWork = useCallback(
    (state: 'working' | 'failed', cardThemeId: StudioThemeId) =>
      router.push(
        SMLRoutes.promoteWork,
        workProgressParams(
          item.uuid,
          state,
          workFilesOf({ assets: kitAssets ?? [], reels: kitReels ?? [], state, themeId: cardThemeId })
        )
      ),
    [item.uuid, kitAssets, kitReels, router]
  );
  const { openReel } = workActions;
  const openCardReady = useCallback(
    (work: StudioCardWork) => {
      if (work.readyAsset !== null) {
        openAsset(work.readyAsset);
      } else if (work.readyReel !== null) {
        openReel(work.readyReel);
      }
    },
    [openAsset, openReel]
  );

  const { state, style, defaultInstructions, instructions, items } = useStudioItems({
    themeId,
    details,
    song: songContext,
    releaseThemeId: songTheme.look.themeId,
  });
  const refusedNoMaster = make.refusedNoMaster || reelActions.songRefusal === 'M24_NO_MASTER';
  const { restart } = draft;
  const { blocksMake, showMissingChoice } = publish;
  const onMake = useCallback(() => {
    if (items === null || themeId === undefined) {
      return;
    }
    if (blocksMake) {
      // The switch is on and the time or the "made for kids" answer is missing.
      showMissingChoice();
      return;
    }
    const name = intl.formatMessage(studioThemeMessages[themeId].name);
    make
      .make(items)
      .then((isMade) => {
        if (isMade) {
          setMade({ name: items.length === 1 ? name : null, count: items.length, uuids: madeUuids.current });
          restart();
        }
      })
      .catch(console.error);
  }, [blocksMake, intl, items, make, restart, showMissingChoice, themeId]);

  // An image has its own step 3 (design studio-details a1), the Spotify header its own picture step
  // (design spotify-header a1); a video and a loop share `DetailsStep`.
  const imageStep = themeId === 'spotifyHeader' ? SpotifyHeaderDetailsStep : ImageDetailsStep;
  const StepDetails = themeId !== undefined && STUDIO_THEMES[themeId].format === 'image' ? imageStep : DetailsStep;

  const songRow = (
    <Panel>
      <StudioSongRow
        item={item}
        onChange={draft.changeSong}
        themeChip={<StudioThemeChip accountId={accountId} item={item} songTheme={songTheme} />}
      />
    </Panel>
  );
  const banner =
    made === null ? null : <StudioMadeBanner made={made} catalogItemUuid={item.uuid} onClose={() => setMade(null)} />;

  return (
    <>
      {songRow}
      {banner}
      <StudioSteps step={step} format={format} themeId={themeId} onBack={draft.back} />
      {(!song.masterReady || refusedNoMaster) && (
        <div id={STUDIO_SONG_FIELD_ID}>
          <Panel>
            <CatalogSongField accountId={accountId} item={item} refusedNoMaster={refusedNoMaster} />
          </Panel>
        </div>
      )}
      {step === 2 && (
        <AssetTypeScreen
          focusFormat={format}
          song={songContext}
          previewTheme={previewTheme}
          isOwner={plan.isOwner}
          onOpenSongLyrics={openLyrics}
          onPick={pickTheme}
          work={cardWork}
          onOpenWork={openCardWork}
          onOpenReady={openCardReady}
          fullExtra={
            <DirectedVideoStudioCard
              accountId={accountId}
              catalogItemUuid={item.uuid}
              hasMaster={item.song.hasMaster}
              masterReady={item.song.masterStatus === 'ready'}
              hasLyrics={item.song.hasLyrics}
              previewTheme={previewTheme}
            />
          }
        />
      )}
      {step === 3 && themeId === 'aiReel' && (
        <AiReelDetails
          accountId={accountId}
          draft={reelDraft}
          look={songTheme.look}
          palette={songTheme.palette}
          locale={songTheme.storyLocale}
          isGenerating={reelActions.isGenerating}
          isUnavailable={reelActions.refusal === 'AI_REELS_UNAVAILABLE'}
          onGenerate={reelActions.generate}
          actionFeedback={reelFeedback}
        />
      )}
      {step === 3 && themeId !== undefined && themeId !== 'aiReel' && themeId !== 'musicVideo' && state !== null && (
        <StepDetails
          accountId={accountId}
          item={item}
          themeId={themeId}
          details={details}
          song={songContext}
          state={state}
          isOwner={plan.isOwner}
          frameLyric={frameLyric}
          onOpenSongLyrics={openLyrics}
          onChange={draft.setDetails}
          songTheme={songTheme}
          style={style}
          instructions={instructions}
          defaultInstructions={defaultInstructions}
          items={items}
          isMaking={make.isMaking}
          actionFeedback={make.feedback}
          isLookSaving={isLookSaving}
          onMake={onMake}
        />
      )}
      {step === 3 && publish.isFullVideo && themeId !== 'musicVideo' && (
        <div id={STUDIO_PUBLISH_FIELD_ID}>
          <StudioPublishSwitch
            accountId={accountId}
            releaseDate={item.releaseDate}
            value={publish.choice}
            onChange={publish.setChoice}
          />
        </div>
      )}
    </>
  );
};

export const StudioSongView = TypedMemo(StudioSongViewComponent);
